import { Check, Zap, ArrowRight } from 'lucide-react';

// Process steps data
const PROCESS_STEPS = [
  {
    id: 'discovery',
    number: '01',
    title: 'Discovery',
    description: 'We start by understanding your business, goals, and audience. This shapes everything that follows.',
    highlights: ['Business audit', 'Goal setting', 'Audience research']
  },
  {
    id: 'strategy',
    number: '02',
    title: 'Strategy & Build',
    description: 'With clear objectives, I develop the strategy and create the assets. Every decision ties back to your goals.',
    highlights: ['Custom strategy', 'Content creation', 'System setup']
  },
  {
    id: 'review',
    number: '03',
    title: 'Review & Refine',
    description: 'You review the work, provide feedback, and we refine until it feels right.',
    highlights: ['Collaborative review', 'Iterative feedback', 'Quality assurance']
  },
  {
    id: 'launch',
    number: '04',
    title: 'Launch & Support',
    description: 'We launch together and I provide ongoing support to ensure everything performs.',
    highlights: ['Smooth launch', 'Performance tracking', 'Ongoing optimization']
  }
];

/**
 * ProcessBackground - Animated background elements
 */
const ProcessBackground = () => (
  <>
    <div className="absolute inset-0 grid-pattern opacity-20" />
    <div className="absolute top-1/4 left-10 w-32 h-32 bg-[#00D4FF]/20 rounded-full blur-3xl animate-blob" />
    <div className="absolute bottom-1/4 right-10 w-40 h-40 bg-[#0EA5E9]/15 rounded-full blur-3xl animate-blob animation-delay-2000" />
    <div className="absolute top-0 left-[10%] w-px h-full bg-gradient-to-b from-transparent via-[#00D4FF]/20 to-transparent" />
    <div className="absolute top-0 right-[10%] w-px h-full bg-gradient-to-b from-transparent via-[#00D4FF]/20 to-transparent" />
  </>
);

/**
 * ProcessHeader - Section header with title
 */
const ProcessHeader = () => (
  <div className="text-center max-w-2xl mx-auto mb-16">
    <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#00D4FF]/10 text-[#00D4FF] text-sm font-medium mb-4 border border-[#00D4FF]/20">
      <Zap size={14} />
      Process
    </span>
    <h2
      data-testid="process-headline"
      className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-4"
      style={{ fontFamily: 'Manrope, sans-serif' }}
    >
      How we{' '}
      <span className="text-gradient">work together</span>
    </h2>
    <p className="text-base md:text-lg leading-relaxed text-gray-400">
      A straightforward process designed to deliver results without unnecessary complexity.
    </p>
  </div>
);

/**
 * ProcessStepHighlights - Highlights list for a step
 */
const ProcessStepHighlights = ({ step }) => (
  <ul className="space-y-2">
    {step.highlights.map((highlight, hIndex) => (
      <li 
        key={`${step.id}-highlight-${hIndex}`} 
        className="flex items-center gap-2 text-sm text-gray-500 group-hover:text-gray-400 transition-all duration-300"
        style={{ transitionDelay: `${hIndex * 50}ms` }}
      >
        <div className="w-5 h-5 rounded-full bg-[#00D4FF]/10 flex items-center justify-center group-hover:bg-[#00D4FF]/20 transition-colors">
          <Check size={12} className="text-[#00D4FF]" />
        </div>
        {highlight}
      </li>
    ))}
  </ul>
);

/**
 * ProcessStepCard - Individual process step card
 */
const ProcessStepCard = ({ step, index }) => (
  <div
    data-testid={`process-step-${step.id}`}
    className="relative group"
    style={{ animationDelay: `${index * 0.1}s` }}
  >
    <div className="bg-[#2D2D3F] rounded-2xl p-6 border border-[#3D3D50] hover:border-[#00D4FF]/50 transition-all duration-500 hover-lift card-shine h-full cursor-pointer">
      {/* Hover glow */}
      <div className="absolute inset-0 rounded-2xl bg-[#00D4FF]/5 opacity-0 group-hover:opacity-100 transition-opacity" />
      
      {/* Number circle with animation */}
      <div className="relative mb-6">
        <div className="w-16 h-16 rounded-full bg-[#00D4FF] flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg shadow-[#00D4FF]/30 group-hover:shadow-[#00D4FF]/50">
          <span className="text-[#1E1E2E] text-xl font-bold">{step.number}</span>
        </div>
        {/* Ping effect on hover */}
        <div className="absolute inset-0 w-16 h-16 rounded-full bg-[#00D4FF] opacity-0 group-hover:opacity-30 group-hover:animate-ping" />
      </div>
      
      {/* Content */}
      <h3
        className="text-xl font-semibold text-white mb-3 group-hover:text-[#00D4FF] transition-colors"
        style={{ fontFamily: 'Manrope, sans-serif' }}
      >
        {step.title}
      </h3>
      <p className="text-sm leading-relaxed text-gray-400 mb-5 group-hover:text-gray-300 transition-colors">
        {step.description}
      </p>
      
      <ProcessStepHighlights step={step} />
      
      {/* Arrow on hover */}
      <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-2 group-hover:translate-x-0">
        <ArrowRight size={20} className="text-[#00D4FF]" />
      </div>
    </div>
  </div>
);

/**
 * ProcessTimeline - The main timeline grid
 */
const ProcessTimeline = () => (
  <div className="relative">
    {/* Animated connection line */}
    <div className="hidden lg:block absolute top-20 left-0 right-0 h-0.5 overflow-hidden">
      <div className="h-full bg-gradient-to-r from-transparent via-[#00D4FF] to-transparent animate-pulse-slow" />
    </div>
    
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      {PROCESS_STEPS.map((step, index) => (
        <ProcessStepCard key={step.id} step={step} index={index} />
      ))}
    </div>
  </div>
);

/**
 * Process - Main process section component
 */
export const Process = () => {
  return (
    <section
      id="process"
      data-testid="process-section"
      className="py-20 md:py-28 bg-[#1E1E2E] relative overflow-hidden"
    >
      <ProcessBackground />

      <div className="max-w-6xl mx-auto px-5 md:px-8 relative z-10">
        <ProcessHeader />
        <ProcessTimeline />
      </div>
    </section>
  );
};

export default Process;
